"use client";

import * as React from "react";
import { VerticalTabs } from "../navigation/vertical-tabs";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/utils/utils";
import { Text } from "../information/text";
import { Button } from "./button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../container/popover";
import { Calendar } from "./calendar";
import {
  type CalenderOption,
  type DateRangeKind,
  type DateRangeValue,
  type YearType,
} from "@/utils/date-range";

// Local range type compatible with react-day-picker range mode
type Range = { from?: Date; to?: Date };

// Preset option lists
const ROLLING: CalenderOption["ROLLING"][] = [
  "past_week",
  "past_month",
  "past_quarter",
  "past_year",
];
const CURRENT: CalenderOption["CURRENT"][] = [
  "current_week",
  "current_month",
  "current_quarter",
  "current_year",
];
const PREVIOUS: CalenderOption["PREVIOUS"][] = [
  "yesterday",
  "previous_week",
  "previous_month",
  "previous_quarter",
  "previous_year",
];
const NEXT: CalenderOption["NEXT"][] = [
  "next_day",
  "next_week",
  "next_month",
  "next_quarter",
  "next_year",
];

// Block props
export type DateRangePickerBlockProps = {
  // Block chrome
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  className?: string;
  labelClassName?: string;
  helpTextClassName?: string;

  // Controlled/uncontrolled value
  value?: DateRangeValue;
  defaultValue?: DateRangeValue;
  onChange?: (next: DateRangeValue) => void;

  // Behavior
  yearTypeDefault?: YearType; // default "calendar_year"
  fiscalYearStartMonth?: number; // default 1 (Jan)
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6; // default 1 (Mon)

  // Hidden form inputs for custom range only
  nameStart?: string;
  nameEnd?: string;
  idStart?: string;
  idEnd?: string;
  required?: boolean;

  // UI
  placeholder?: string;
  triggerClassName?: string;
  panelClassName?: string;

  // Button props passthrough
} & Omit<
  React.ComponentProps<typeof Button>,
  "onChange" | "value" | "defaultValue" | "children" | "required"
>;

// Utility to create a sensible default value
function makeDefaultValue(yearType: YearType): DateRangeValue {
  return {
    kind: "ROLLING",
    value: "past_week",
    yearType,
  };
}

// Local label helpers (title-cased, e.g., "next_quarter" -> "Next Quarter")
function toTitle(s: string): string {
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
}
function optionLabel(kind: DateRangeKind, opt: string): string {
  // Fallback: prettify token; this block does not compute concrete dates for non-CUSTOM
  switch (kind) {
    case "ROLLING":
    case "CURRENT":
    case "PREVIOUS":
    case "NEXT":
      return toTitle(opt.replaceAll("_", " "));
    default:
      return "Custom period";
  }
}
function fmtDate(d: Date): string {
  try {
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
}
function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Inline RangeCalendar built on the shared Calendar wrapper
function RangeCalendar({
  selected,
  onSelect,
  className,
  numberOfMonths = 2,
  ...props
}: {
  selected?: Range;
  onSelect?: (range?: Range) => void;
  className?: string;
  numberOfMonths?: number;
} & Omit<
  React.ComponentProps<typeof Calendar>,
  "mode" | "selected" | "onSelect" | "className" | "numberOfMonths"
>) {
  return (
    <Calendar
      mode="range"
      selected={selected as any}
      onSelect={onSelect as any}
      numberOfMonths={numberOfMonths}
      pagedNavigation
      className={cn("p-3", className)}
      {...(props as any)}
    />
  );
}

export const DateRangePickerBlock = React.forwardRef<
  HTMLInputElement,
  DateRangePickerBlockProps
>(
  (
    {
      // Block chrome
      label,
      helpText,
      className,
      labelClassName,
      helpTextClassName,

      // Picker API
      value,
      defaultValue,
      onChange,
      yearTypeDefault = "calendar_year",
      fiscalYearStartMonth = 1,
      weekStartsOn = 1,

      nameStart,
      nameEnd,
      idStart,
      idEnd,
      required,

      placeholder = "Select date range",
      triggerClassName,
      panelClassName,
      disabled,
      id, // for label association, used to derive hidden ids
      ...buttonProps
    },
    _ref
  ) => {
    const generatedId = React.useId();
    const resolvedId = id ?? generatedId;
    const helpId = helpText ? `${resolvedId}-help` : undefined;

    // Controlled / uncontrolled value
    const isControlled = value !== undefined;
    const [internal, setInternal] = React.useState<DateRangeValue>(
      defaultValue ?? makeDefaultValue(yearTypeDefault)
    );
    const current = isControlled ? (value as DateRangeValue) : internal;

    // Local settings (yearType can be changed in Settings tab)
    const [yearType, setYearType] = React.useState<YearType>(
      current.yearType ?? yearTypeDefault
    );

    // Tabs
    type TabVal = DateRangeKind | "SETTINGS";
    const [tab, setTab] = React.useState<TabVal>("ROLLING");

    // Popover
    const [open, setOpen] = React.useState(false);

    // Display string
    // - Non-CUSTOM: show title-cased preset label like "Next Quarter"
    // - CUSTOM: show picked date range
    const display = React.useMemo(() => {
      if (current.kind === "CUSTOM") {
        if (current.startDate && current.endDate) {
          return `${fmtDate(current.startDate)} – ${fmtDate(current.endDate)}`;
        }
        return undefined;
      }
      const opt = current.value as string | undefined;
      if (!opt) return undefined;
      return optionLabel(current.kind, opt);
    }, [current]);

    // Hidden inputs only for CUSTOM
    const startEnd = React.useMemo(() => {
      if (current.kind === "CUSTOM" && current.startDate && current.endDate) {
        return { start: current.startDate, end: current.endDate };
      }
      return { start: undefined, end: undefined };
    }, [current]);

    // Emit change
    const emitChange = (next: DateRangeValue) => {
      if (isControlled) {
        onChange?.(next);
      } else {
        setInternal(next);
        onChange?.(next);
      }
    };

    // Preset list
    function PresetList<T extends string>({
      items,
      kind,
      active,
      onPick,
    }: {
      items: readonly T[];
      kind: DateRangeKind;
      active?: string;
      onPick: (opt: T) => void;
    }) {
      return (
        <div className="grid gap-2">
          {items.map((opt) => {
            const isActive = active === opt;
            return (
              <Button
                key={opt}
                type="button"
                variant={isActive ? "solid" : "outline"}
                
                aria-pressed={isActive}
                onClick={() => onPick(opt)}
              >
                {optionLabel(kind, opt)}
              </Button>
            );
          })}
        </div>
      );
    }

    // Custom tab local state
    const [pendingRange, setPendingRange] = React.useState<Range>(() => {
      if (current.kind === "CUSTOM" && current.startDate && current.endDate) {
        return { from: current.startDate, to: current.endDate };
      }
      return { from: undefined, to: undefined };
    });

    const onApplyCustom = () => {
      if (!pendingRange.from || !pendingRange.to) return;
      const next: DateRangeValue = {
        kind: "CUSTOM",
        value: "custom_period",
        yearType,
        startDate: pendingRange.from,
        endDate: pendingRange.to,
      };
      emitChange(next);
      setOpen(false);
    };

    const onClearCustom = () => {
      setPendingRange({ from: undefined, to: undefined });
    };

    const activeKind = current.kind;
    const activeOpt = current.value as string | undefined;

    return (
      <div className={cn("flex flex-col gap-0.5", className)}>
        {label ? (
          <label
            htmlFor={resolvedId}
            className={cn("text-caption font-medium", labelClassName)}
          >
            {label}
          </label>
        ) : null}

        {/* Hidden inputs for forms if names provided (CUSTOM only) */}
        {nameStart ? (
          <input
            type="hidden"
            id={idStart ?? `${resolvedId}-start`}
            name={nameStart}
            required={required}
            value={startEnd.start ? toISO(startEnd.start) : ""}
            readOnly
            aria-hidden="true"
          />
        ) : null}
        {nameEnd ? (
          <input
            type="hidden"
            id={idEnd ?? `${resolvedId}-end`}
            name={nameEnd}
            required={required}
            value={startEnd.end ? toISO(startEnd.end) : ""}
            readOnly
            aria-hidden="true"
          />
        ) : null}

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="w-full border px-4 h-8 rounded-lg text-body flex space-x-1 items-center">
                <CalendarIcon className="mr-2 h-4 w-4" />
              <Text>
                {display ?? placeholder ?? "Select date range"}
              </Text>
            </div>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            className={cn("w-max  p-0", panelClassName)}
          >
            <VerticalTabs
              value={tab}
              listClassName="w-28" 
              onValueChange={(v) => setTab(v as TabVal)}
              verticalText={false}
              items={[
                {
                  value: "ROLLING",
                  label: "Rolling",
                  content: (
                    <div className="p-3">
                      <PresetList
                        items={ROLLING}
                        kind="ROLLING"
                        active={
                          activeKind === "ROLLING" ? activeOpt : undefined
                        }
                        onPick={(opt) => {
                          emitChange({ kind: "ROLLING", value: opt, yearType });
                          setOpen(false);
                        }}
                      />
                    </div>
                  ),
                },
                {
                  value: "CURRENT",
                  label: "Current",
                  content: (
                    <div className="p-3">
                      <PresetList
                        items={CURRENT}
                        kind="CURRENT"
                        active={
                          activeKind === "CURRENT" ? activeOpt : undefined
                        }
                        onPick={(opt) => {
                          emitChange({ kind: "CURRENT", value: opt, yearType });
                          setOpen(false);
                        }}
                      />
                    </div>
                  ),
                },
                {
                  value: "PREVIOUS",
                  label: "Previous",
                  content: (
                    <div className="p-3">
                      <PresetList
                        items={PREVIOUS}
                        kind="PREVIOUS"
                        active={
                          activeKind === "PREVIOUS" ? activeOpt : undefined
                        }
                        onPick={(opt) => {
                          emitChange({
                            kind: "PREVIOUS",
                            value: opt,
                            yearType,
                          });
                          setOpen(false);
                        }}
                      />
                    </div>
                  ),
                },
                {
                  value: "NEXT",
                  label: "Next",
                  content: (
                    <div className="p-3">
                      <PresetList
                        items={NEXT}
                        kind="NEXT"
                        active={activeKind === "NEXT" ? activeOpt : undefined}
                        onPick={(opt) => {
                          emitChange({ kind: "NEXT", value: opt, yearType });
                          setOpen(false);
                        }}
                      />
                    </div>
                  ),
                },
                {
                  value: "CUSTOM",
                  label: "Custom",
                  content: (
                    <div className="space-y-3">
                      <RangeCalendar
                        selected={pendingRange}
                        onSelect={(r?: Range) => setPendingRange(r ?? {})}
                        numberOfMonths={2}
                      />
                      <div className="flex justify-center gap-2 py-2 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          color="destructive"
                          onClick={onClearCustom}
                        >
                          Clear
                        </Button>
                        <Button
                          type="button"
                          onClick={onApplyCustom}
                          disabled={!pendingRange.from || !pendingRange.to}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  ),
                },
                {
                  value: "SETTINGS",
                  label: "Settings",
                  content: (
                    <div className="p-3">
                      <fieldset className="space-y-2">
                        <legend className="text-sm font-medium">
                          Year type
                        </legend>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="yearType"
                            value="calendar_year"
                            checked={yearType === "calendar_year"}
                            onChange={() => setYearType("calendar_year")}
                          />
                          Calendar year
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="yearType"
                            value="fiscal_year"
                            checked={yearType === "fiscal_year"}
                            onChange={() => setYearType("fiscal_year")}
                          />
                          Fiscal year (start month {fiscalYearStartMonth})
                        </label>
                        <p className="text-xs text-muted-foreground">
                          The year type influences Current/Previous/Next
                          quarter/year ranges.
                        </p>
                      </fieldset>
                    </div>
                  ),
                },
              ]}
            />
          </PopoverContent>
        </Popover>

        {helpText ? (
          <Text
            as="p"
            id={helpId}
            className={cn("text-xs text-muted-foreground", helpTextClassName)}
          >
            {helpText}
          </Text>
        ) : null}
      </div>
    );
  }
);

DateRangePickerBlock.displayName = "DateRangePickerBlock";

export default DateRangePickerBlock;
